import type { Api } from "../api";
import { empty, h, label, pill } from "../dom";
import { money, qty, shortDate, today, unitPrice, when } from "../format";
import { lineFigures, LOW_GM_PCT, orderFigures, releaseDay, releaseFlag, viewMode, words } from "../orders";
import type { FullOrder, HistoryEntry, Me, OrderHistory, Page, QbEntry } from "../types";
import { entryState, kindWords } from "../quickbooks";
import { card as qbCard } from "./quickbooks";
import { historySection } from "./history";
import { can, crumbs, modeNotice } from "./shared";
import { plain } from "../words";

// One customer PO (the agreed "Order" screen): figures, lines at price and cost with GM, releases with what needs a
// look, and beside them the vendor POs, approvals, changes and open exceptions (get_order). Cost and margin are
// internal and say so.

const VPO_TONE: Record<string, "late" | "soon" | "good" | "neutral"> = {
  DRAFT: "late", AWAITING_APPROVAL: "soon", APPROVED: "soon", SENT: "good", ACKNOWLEDGED: "good",
};
const pct = (n: number | undefined) => (n === undefined ? "" : `${n.toFixed(1)}%`);
const show = (v: unknown) => (v === null || v === undefined ? "" : typeof v === "string" ? v : JSON.stringify(v));

export async function order(api: Api, me: Me, customerPoId: number, query = new URLSearchParams()): Promise<HTMLElement> {
  const view = viewMode(query, me.mode);
  const [o, history, qb] = await Promise.all([
    api.tool<FullOrder & { error?: string }>("get_order", { customer_po_id: customerPoId, ...(view.other ? { mode: view.mode } : {}) }),
    api.tool<OrderHistory>("order_history", { customer_po_id: customerPoId }).then((r) => r.history ?? [], () => [] as HistoryEntry[]),
    // Its QuickBooks entries (phase 5: bills, the customer invoice, credits), for those who see them (costs.read).
    can(me, "invoices.read") && can(me, "costs.read")
      ? api.tool<Page<QbEntry>>("qb_entries", { customer_po_id: customerPoId, status: "ALL", limit: 100 }).then((r) => r.rows, () => [] as QbEntry[])
      : Promise.resolve(null),
  ]);
  if (!o.customerPoId) return h("div", { class: "page" }, h("p", { class: "lede" }, o.error ?? "There's no such order."));

  const lines = lineFigures(o);
  const f = orderFigures(lines);
  const supplier = o.vendorPos.find((v) => v.status !== "CLOSED")?.supplier?.name ?? "";
  const day = today();
  const live = o.releases.filter((r) => r.status !== "WITHDRAWN");
  const withdrawn = o.releases.length - live.length;
  const customer = o.customer?.name ?? "Customer";
  const t = o.totals;

  const tile = (name: string, figure: string, note: string, tone?: "late") =>
    h("div", { class: "tile" }, label(name), h("span", { class: `number-md${tone ? " late" : ""}` }, figure), h("span", { class: "caption" }, note));

  return h("div", { class: "page" },
    crumbs({ text: "Orders", href: "/orders" }, { text: customer }, { text: o.poNumber, num: true }),
    h("div", { class: "stack-tight" },
      label(`Customer PO · ${words(o.status).toLowerCase()}${o.closeKind ? ` (${o.closeKind.toLowerCase()})` : ""} · dated ${shortDate(o.poDate)}`),
      h("h1", { class: "title" }, `${customer} · PO ${o.poNumber}`),
      o.endCustomer ? h("p", { class: "lede" }, `For ${o.endCustomer.name}.`) : null,
      o.requestedDelivery ? h("p", { class: "lede" }, `Requested for ${shortDate(o.requestedDelivery)}${live.length ? `; ${live.length} ${live.length === 1 ? "release" : "releases"} scheduled` : ""}.`) : null),
    view.other ? modeNotice(view.mode, me.mode) : o.orderMode === "SIMULATION" ? h("div", { class: "notice" }, "A simulation copy: nothing done to it reaches the customer or supplier.") : null,

    h("div", { class: "tiles tiles-6" },
      tile("Ordered", qty(t.orderedEa, ""), `EA, ${t.lines} ${t.lines === 1 ? "line" : "lines"}`),
      tile("Released", qty(t.releasedEa, ""), `EA in ${live.length} ${live.length === 1 ? "release" : "releases"}`),
      tile("Shipped", qty(t.shippedEa, ""), `EA; ${qty(t.deliveredEa, "")} delivered`),
      tile("Sell", money(f.sell), live.length ? `${money(f.sell / Math.max(live.length, 1))} a release` : "at the customer's price"),
      tile("Cost", f.cost === undefined ? "—" : money(f.cost), f.cost === undefined ? "not every line has a vendor PO" : "internal"),
      tile("Gross margin", f.gmPct === undefined ? "—" : pct(f.gmPct), f.gm === undefined ? "" : money(f.gm))),

    h("div", { class: "layout" },
      h("div", { class: "main" },
        h("section", { class: "stack", "aria-labelledby": "lines" },
          h("div", { class: "section-head" }, h("h2", { id: "lines", class: "heading" }, "Lines"),
            h("span", { class: "note" }, "Cost and GM internal: never in anything a customer sees")),
          h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "#"), h("th", { scope: "col" }, "Part"), h("th", { scope: "col", class: "r" }, "Qty EA"),
              h("th", { scope: "col", class: "r" }, "Price"), h("th", { scope: "col", class: "r" }, "Cost"), h("th", { scope: "col", class: "r" }, "GM"))),
            h("tbody", null, lines.map((l) => h("tr", null,
              h("td", { class: "num muted" }, l.lineNo),
              h("td", null, h("span", { class: "num" }, l.customerItemNo ?? ""), " ", l.description ?? "",
                h("div", { class: "num caption" }, [l.sku, l.vendorPartNo ? `${l.supplier} ${l.vendorPartNo}` : null].filter(Boolean).join(" · "))),
              h("td", { class: "num r" }, qty(l.qtyEa, "")),
              h("td", { class: "num r" }, unitPrice(l.price)),
              h("td", { class: "num r" }, l.cost === undefined ? "" : unitPrice(l.cost)),
              h("td", { class: "num r" }, l.gmPct !== undefined && l.gmPct < LOW_GM_PCT ? pill(`${pct(l.gmPct)} low`, "late") : pct(l.gmPct))))),
            h("tfoot", null, h("tr", null, h("td", { colspan: 2 }, "Total"), h("td", { class: "num r" }, qty(t.orderedEa, "")),
              h("td", { class: "num r" }, money(f.sell)), h("td", { class: "num r" }, f.cost === undefined ? "" : money(f.cost)),
              h("td", { class: "num r" }, pct(f.gmPct)))))),
          h("p", { class: "caption" }, `Price and cost are per EA; GM below ${LOW_GM_PCT}% is marked.`)),

        h("section", { class: "stack", "aria-labelledby": "releases" },
          h("div", { class: "section-head" }, h("h2", { id: "releases", class: "heading" }, "Releases"),
            withdrawn ? h("span", { class: "note" }, `${withdrawn} withdrawn, shown last`) : null),
          o.releases.length
            ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
                h("thead", null, h("tr", null,
                  h("th", { scope: "col" }, "#"), h("th", { scope: "col" }, "Day"), h("th", { scope: "col" }, "Status"),
                  h("th", { scope: "col", class: "r" }, "Qty EA"), h("th", { scope: "col", class: "r" }, "Value"), h("th", { scope: "col" }, "Note"))),
                h("tbody", null, [...live, ...o.releases.filter((r) => r.status === "WITHDRAWN")].map((r) => {
                  const flag = releaseFlag(r, day, supplier);
                  const docs = [r.carrier, r.bol && `BOL ${r.bol}`, r.pro && `PRO ${r.pro}`].filter(Boolean).join(" · ");
                  return h("tr", { class: r.status === "WITHDRAWN" ? "withdrawn" : null },
                    h("td", { class: "num muted" }, r.releaseNo),
                    h("td", { class: "num nowrap" }, shortDate(releaseDay(r)), r.plannedDelivery || r.confirmedDelivery ? null : h("div", { class: "caption" }, "pickup")),
                    h("td", { class: "nowrap" }, words(r.status), r.vendorConfirmedAt ? h("div", { class: "caption" }, `${supplier || "Supplier"} confirmed`) : null),
                    h("td", { class: "num r" }, qty(r.qtyEa, "")),
                    h("td", { class: "num r" }, money(r.sellValue)),
                    h("td", null, flag ? pill(flag.text, flag.tone) : null, docs ? h("div", { class: "num caption" }, docs) : null,
                      r.shippedOn ? h("div", { class: "caption" }, `Shipped ${shortDate(r.shippedOn)}`) : null));
                }))))
            : empty("No releases", "Releases against this PO appear here once they're scheduled.")),
        qb?.length
          ? h("section", { class: "stack", "aria-labelledby": "qb" }, h("h2", { id: "qb", class: "heading" }, "QuickBooks"),
              qb.map((e) => qbCard(api, me, e)))
          : null,
        historySection(history, "What happens to this order and its vendor POs is recorded here.")),

      h("aside", { class: "side", "aria-label": "Order details" },
        o.vendorPos.length
          ? o.vendorPos.map((v) => h("div", { class: "panel facts" },
              label("Vendor PO"),
              h("div", { class: "row" }, h("span", { class: "num ink big" }, v.number), h("span", null, v.supplier?.name ?? "")),
              h("div", null, pill(words(v.status).toLowerCase(), VPO_TONE[v.status] ?? "neutral")),
              h("span", { class: "caption" }, [v.orderedOn ? `Ordered ${shortDate(v.orderedOn)}` : null,
                v.approvedAt ? `approved ${shortDate(v.approvedAt)}` : null, v.sentAt ? `sent ${shortDate(v.sentAt)}` : null,
                (v.lines ?? []).every((l) => typeof l.unitCost === "number")
                  ? `${money((v.lines ?? []).reduce((s, l) => s + l.qtyEa * (l.unitCost ?? 0), 0))} at cost` : null].filter(Boolean).join(" · ")),
              v.status === "AWAITING_APPROVAL"
                ? h("a", { href: `/vendor-po/${o.customerPoId}/${v.vendorPoId}${view.param}` }, me.permissions.includes("vendor_pos.approve") ? "Review the approval" : "See the approval")
                : h("a", { href: `/vendor-po/${o.customerPoId}/${v.vendorPoId}${view.param}` }, "See the vendor PO")))
          : h("div", { class: "empty" }, label("Vendor PO"), h("span", null, "None yet.")),
        h("div", { class: "panel facts" },
          label("Customer approval"),
          h("span", null, approvals(o)),
          label("Changes"),
          o.changes.length
            ? h("ul", { class: "plain" }, o.changes.slice(0, 10).map((c) => h("li", null,
                h("span", { class: "caption" }, `${when(c.appliedAt)}${c.by ? ` · ${c.by}` : ""}`), h("br"),
                `${words(c.kind)}${c.field ? `: ${c.field}` : ""} `, h("span", { class: "num" }, `${show(c.before)} → ${show(c.after)}`))))
            : h("span", { class: "muted" }, "None recorded."),
          label("Exceptions"),
          o.openExceptions.length
            ? h("ul", { class: "plain" }, o.openExceptions.map((e) => h("li", null,
                pill(e.urgency.toLowerCase(), e.urgency === "CRITICAL" ? "late" : e.urgency === "ELEVATED" ? "soon" : "neutral"), " ",
                h("a", { href: `/exception/${e.exceptionId}` }, e.message ? plain(e.message) : words(e.kind)))))
            : h("span", { class: "muted" }, "None open."),
          label("Invoices"),
          qb === null
            ? h("span", { class: "muted" }, "Bills and invoices need the costs permission to see.")
            : qb.length
              ? h("ul", { class: "plain" }, qb.map((e) => h("li", null, h("a", { href: "#qb" }, kindWords(e.kind)), " ",
                  h("span", { class: "num" }, [e.docNumber, money(e.amount)].filter(Boolean).join(" · ")), " ",
                  pill(entryState(e.status).text.toLowerCase(), entryState(e.status).tone))))
              : h("span", { class: "muted" }, "None yet: bills come from the supplier's invoices, the customer invoice from each bill.")),
        o.notes ? h("div", { class: "empty caption" }, o.notes) : null)));
}

/** "All 15 approved by Customer PO 14033 (release schedule), Wed 9 Sep", or how many are still to approve. */
function approvals(o: FullOrder): string {
  const live = o.releases.filter((r) => r.status !== "WITHDRAWN");
  if (!live.length) return "No releases yet.";
  const done = live.filter((r) => r.customerApprovedAt);
  if (done.length < live.length) return `${done.length} of ${live.length} releases approved; ${live.length - done.length} to approve.`;
  const by = new Set(done.map((r) => r.customerApprovedBy ?? ""));
  const first = done.map((r) => r.customerApprovedAt!).sort()[0];
  return `All ${live.length} approved${by.size === 1 && [...by][0] ? ` by ${[...by][0]}` : ""}, ${shortDate(first)}.`;
}
